import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Trophy, 
  Target, 
  Calendar,
  User,
  Hash,
  Mail,
  Crown,
  Star,
  Check,
  Zap,
  Loader2,
  Bell,
  BellOff,
  Share2,
  Copy,
  Award,
  UserPlus,
  Users
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import { PerksCard } from '@/components/PerksCard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { member, refreshMember, updateMember } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchProfile();
    
    // Check if returning from successful payment
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(
        `${API_URL}/payments/session-status/${sessionId}?member_id=${member?.id}`
      );
      
      if (response.data.status === 'success') {
        toast.success('Welcome to TGA Pro! Your membership is now active.');
        // Refresh member data to get updated membership tier
        await refreshMember();
        fetchProfile();
        // Clear the URL params
        navigate('/profile', { replace: true });
      }
    } catch (error) {
      console.error('Payment verification error:', error);
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      const response = await axios.post(`${API_URL}/payments/create-checkout`, {
        member_id: member.id,
        plan: plan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
      setUpgrading(false);
    }
  };

  const fetchProfile = async () => {
    if (!member?.id) return;
    
    try {
      const response = await axios.get(`${API_URL}/members/${member.id}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatScoreToPar = (score) => {
    if (score === 0) return 'E';
    if (score > 0) return `+${score}`;
    return score.toString();
  };

  const getMembershipBadge = () => {
    const tier = profile?.membership_tier || 'amateur';
    if (tier === 'pro') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/50">
          <Crown className="w-4 h-4" />
          Pro Member
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-muted text-muted-foreground border border-white/10">
        <User className="w-4 h-4" />
        Amateur
      </span>
    );
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sponsor Banner - Top of Page */}
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-10 object-contain"
          />
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
            <h1 className="text-3xl sm:text-4xl font-heading text-white">
              My Profile
            </h1>
            {getMembershipBadge()}
          </div>
          <p className="text-muted-foreground">
            View your TGA membership details and tournament history
          </p>
        </div>

        <div className="grid gap-6">
          {/* Membership Card - Show upgrade option for Amateur members */}
          {profile?.membership_tier !== 'pro' && (
            <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border-amber-500/30 overflow-hidden">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Crown className="w-6 h-6 text-amber-400" />
                  <CardTitle className="font-heading text-xl text-amber-400">
                    Upgrade to Pro
                  </CardTitle>
                </div>
                <CardDescription className="text-muted-foreground">
                  Unlock exclusive benefits — 50% off entries, affiliate earnings, exclusive merch & more
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* Monthly Plan */}
                  <div className="p-5 bg-card/50 rounded-xl border border-white/10 hover:border-amber-500/50 transition-colors">
                    <div className="text-center mb-4">
                      <p className="text-muted-foreground text-sm mb-1">Monthly</p>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-mono font-bold text-white">$5</span>
                        <span className="text-muted-foreground">/month</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Auto-renews monthly</p>
                    </div>
                    <Button
                      onClick={() => handleUpgrade('pro_monthly')}
                      disabled={upgrading}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-pill"
                      data-testid="upgrade-monthly-btn"
                    >
                      {upgrading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Go Pro Monthly
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Yearly Plan */}
                  <div className="p-5 bg-card/50 rounded-xl border-2 border-amber-500/50 relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
                        BEST VALUE
                      </span>
                    </div>
                    <div className="text-center mb-4">
                      <p className="text-muted-foreground text-sm mb-1">Yearly</p>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-mono font-bold text-white">$50</span>
                        <span className="text-muted-foreground">/year</span>
                      </div>
                      <p className="text-xs text-green-400 mt-1">Save $10 vs monthly</p>
                    </div>
                    <Button
                      onClick={() => handleUpgrade('pro_yearly')}
                      disabled={upgrading}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-semibold rounded-pill"
                      data-testid="upgrade-yearly-btn"
                    >
                      {upgrading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Star className="w-4 h-4 mr-2" />
                          Go Pro Yearly
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Pro Benefits */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-white mb-3">Pro Player Benefits:</p>
                  <div className="grid gap-2.5 text-sm">
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span><strong className="text-white">50% off</strong> all tournament entry fees</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span><strong className="text-white">15% discount code</strong> for purchases on the TGA website</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span><strong className="text-white">Affiliate program</strong> — earn 20% commission on sales from your referral code</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span><strong className="text-white">Exclusive merch</strong> — purchase the official Pro Player pin & Pro Tour t-shirt</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span>Access to exclusive Pro Tour tournaments with cash & prizes</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span>Pro badge on leaderboards</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pro Membership Status - Show for Pro members */}
          {profile?.membership_tier === 'pro' && (
            <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border-amber-500/30">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Crown className="w-6 h-6 text-amber-400" />
                  <CardTitle className="font-heading text-xl text-amber-400">
                    Pro Membership Active
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-white mb-1">
                      You have full access to all Pro Tour tournaments!
                    </p>
                    {profile?.membership_expiry && (
                      <p className="text-muted-foreground text-sm">
                        Your membership renews on {formatExpiryDate(profile.membership_expiry)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-green-400">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Active</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Member Info Card */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Member Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <User className="w-4 h-4" />
                    Name
                  </div>
                  <p className="text-white text-lg font-medium">{profile?.name}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Hash className="w-4 h-4" />
                    Member Number
                  </div>
                  <p className="text-primary text-lg font-mono font-bold">{profile?.member_number}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Mail className="w-4 h-4" />
                    Email
                  </div>
                  <p className="text-white text-lg">{profile?.email}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Member Since
                  </div>
                  <p className="text-white text-lg">{formatDate(profile?.created_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>


          {/* Email Preferences */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Email Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  {profile?.email_notifications !== false ? (
                    <Bell className="w-5 h-5 text-secondary" />
                  ) : (
                    <BellOff className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-white font-medium">Tour event notifications</p>
                    <p className="text-muted-foreground text-sm">
                      Get emailed when a new event is added to a tour you've played in
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !(profile?.email_notifications !== false);
                    try {
                      await axios.put(`${API_URL}/members/${member.id}/email-preferences`, {
                        email_notifications: newVal
                      });
                      setProfile(prev => ({ ...prev, email_notifications: newVal }));
                      toast.success(newVal ? 'Notifications enabled' : 'Notifications disabled');
                    } catch {
                      toast.error('Failed to update preferences');
                    }
                  }}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    profile?.email_notifications !== false
                      ? 'bg-secondary'
                      : 'bg-zinc-700'
                  }`}
                  role="switch"
                  aria-checked={profile?.email_notifications !== false}
                  data-testid="email-notifications-toggle"
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                      profile?.email_notifications !== false
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>


          {/* Invite Friends */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary" />
                Invite Friends
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm mb-4">
                Share your invite link. When someone joins through it, you both earn a badge!
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm text-white truncate" data-testid="invite-link">
                  {`${window.location.origin}/auth?ref=${profile?.member_number}`}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-primary/50 hover:bg-primary/20 shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/auth?ref=${profile?.member_number}`);
                    toast.success('Invite link copied!');
                  }}
                  data-testid="copy-invite-link-btn"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
              </div>
              {(profile?.referral_count || 0) > 0 && (
                <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4 text-secondary" />
                  <span>
                    You've invited <span className="text-white font-semibold">{profile.referral_count}</span> {profile.referral_count === 1 ? 'member' : 'members'}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Perks & Rewards (drink tickets) */}
          <PerksCard memberId={member?.id} />

          {/* Badges */}
          {(profile?.badges?.length > 0 || profile?.referred_by_name) && (
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Badges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {profile?.badges?.map((badge, i) => (
                    <div
                      key={i}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${
                        badge.type === 'gold_trophy'
                          ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40'
                          : badge.type === 'silver_trophy'
                          ? 'bg-zinc-300/15 text-zinc-200 border-zinc-300/40'
                          : badge.type === 'bronze_trophy'
                          ? 'bg-amber-700/15 text-amber-500 border-amber-700/40'
                          : badge.type === 'recruiter'
                          ? 'bg-purple-500/15 text-purple-400 border-purple-500/30'
                          : 'bg-secondary/15 text-secondary border-secondary/30'
                      }`}
                      data-testid={`badge-${badge.type}`}
                    >
                      {badge.type === 'gold_trophy' || badge.type === 'silver_trophy' || badge.type === 'bronze_trophy' ? (
                        <Trophy className="w-4 h-4" />
                      ) : badge.type === 'recruiter' ? (
                        <UserPlus className="w-4 h-4" />
                      ) : (
                        <Award className="w-4 h-4" />
                      )}
                      {badge.label}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Card */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Trophy className="w-5 h-5 text-primary" />
                Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pro Tour stats */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="text-xs uppercase tracking-wider text-primary font-medium">Pro Tour</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-6 bg-muted rounded-lg text-center">
                    <Trophy className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-muted-foreground text-sm mb-1">Tournaments Played</p>
                    <p className="text-4xl font-mono font-bold text-white" data-testid="stat-pro-tournaments">
                      {profile?.tournaments_played || 0}
                    </p>
                  </div>
                  <div className="p-6 bg-muted rounded-lg text-center">
                    <Target className="w-8 h-8 text-secondary mx-auto mb-2" />
                    <p className="text-muted-foreground text-sm mb-1">Best Score</p>
                    <p className={`text-4xl font-mono font-bold ${
                      profile?.best_score !== null && profile?.best_score < 0
                        ? 'text-secondary text-glow-green'
                        : profile?.best_score === 0
                          ? 'text-primary'
                          : 'text-white'
                    }`} data-testid="stat-pro-best">
                      {profile?.best_score !== null && profile?.best_score !== undefined ? formatScoreToPar(profile.best_score) : '--'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal Event stats */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-secondary" />
                  <span className="text-xs uppercase tracking-wider text-secondary font-medium">Personal Events</span>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-5 bg-muted/60 rounded-lg text-center border border-white/5">
                    <Users className="w-7 h-7 text-secondary/70 mx-auto mb-2" />
                    <p className="text-muted-foreground text-xs mb-1">Events Played</p>
                    <p className="text-3xl font-mono font-bold text-white" data-testid="stat-personal-tournaments">
                      {profile?.personal_tournaments_played || 0}
                    </p>
                  </div>
                  <div className="p-5 bg-muted/60 rounded-lg text-center border border-white/5">
                    <Target className="w-7 h-7 text-secondary/70 mx-auto mb-2" />
                    <p className="text-muted-foreground text-xs mb-1">Best Score</p>
                    <p className={`text-3xl font-mono font-bold ${
                      profile?.personal_best_score !== null && profile?.personal_best_score !== undefined && profile?.personal_best_score < 0
                        ? 'text-secondary'
                        : 'text-white'
                    }`} data-testid="stat-personal-best">
                      {profile?.personal_best_score !== null && profile?.personal_best_score !== undefined ? formatScoreToPar(profile.personal_best_score) : '--'}
                    </p>
                  </div>
                  <div className="p-5 bg-muted/60 rounded-lg text-center border border-white/5">
                    <Trophy className="w-7 h-7 text-zinc-300/70 mx-auto mb-2" />
                    <p className="text-muted-foreground text-xs mb-1">Wins</p>
                    <p className="text-3xl font-mono font-bold text-white" data-testid="stat-personal-wins">
                      {profile?.personal_wins || 0}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tournament History */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Tournament History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {profile?.tournament_history?.length > 0 ? (
                <div className="space-y-3">
                  {profile.tournament_history.map((tournament, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-muted rounded-lg cursor-pointer card-hover"
                      onClick={() => navigate(`/tournaments/${tournament.tournament_id}`)}
                      data-testid={`history-item-${index}`}
                    >
                      <div>
                        <p className="text-white font-medium">{tournament.tournament_name}</p>
                        <p className="text-muted-foreground text-sm">
                          {formatDate(tournament.date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono text-lg">{tournament.total_strokes} strokes</p>
                        <p className={`font-mono font-bold ${
                          tournament.score_to_par < 0 ? 'text-secondary' :
                          tournament.score_to_par === 0 ? 'text-primary' :
                          'text-destructive'
                        }`}>
                          {formatScoreToPar(tournament.score_to_par)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No tournament history yet</p>
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    variant="link"
                    className="text-primary mt-2"
                  >
                    Join a tournament to get started
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
